import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { WerklijstenData } from "../types/werklijst";
import { fetchWerklijsten } from "../lib/api/werklijsten";
import { useAuth } from "./AuthContext";

interface WerklijstenState {
  werklijstenData: WerklijstenData;
  isLoadingWerklijsten: boolean;
  error: string | null;
  refreshWerklijsten: () => Promise<void>;
}

const WerklijstenContext = createContext<WerklijstenState>({
  werklijstenData: {},
  isLoadingWerklijsten: false,
  error: null,
  refreshWerklijsten: async () => {},
});

export function WerklijstenProvider({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  const [werklijstenData, setWerklijstenData] = useState<WerklijstenData>({});
  const [isLoadingWerklijsten, setIsLoadingWerklijsten] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load werklijsten data
  const loadWerklijsten = useCallback(async () => {
    if (!token) {
      setIsLoadingWerklijsten(false);
      return;
    }

    setIsLoadingWerklijsten(true);
    setError(null);

    try {
      const werklijsten = await fetchWerklijsten();
      
      // Convert array to object keyed by date
      const werklijstenByDate = werklijsten.reduce((acc, entry) => {
        acc[entry.date] = entry;
        return acc;
      }, {} as WerklijstenData);
      
      setWerklijstenData(werklijstenByDate);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load werklijsten";
      setError(errorMessage);
      console.warn("Failed to load werklijsten data:", err);
      setWerklijstenData({});
    } finally {
      setIsLoadingWerklijsten(false);
    }
  }, [token]);

  // Public method to refresh werklijsten
  const refreshWerklijsten = useCallback(async () => {
    await loadWerklijsten();
  }, [loadWerklijsten]);

  // Load werklijsten on mount and when token changes
  useEffect(() => {
    if (token) {
      loadWerklijsten();
    } else {
      setWerklijstenData({});
      setIsLoadingWerklijsten(false);
      setError(null);
    }
  }, [token, loadWerklijsten]);

  return (
    <WerklijstenContext.Provider
      value={{
        werklijstenData,
        isLoadingWerklijsten,
        error,
        refreshWerklijsten,
      }}
    >
      {children}
    </WerklijstenContext.Provider>
  );
}

export function useWerklijsten() {
  const context = useContext(WerklijstenContext);
  if (!context) {
    throw new Error("useWerklijsten must be used within a WerklijstenProvider");
  }
  return context;
}
